import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { isAfter, isPast, subDays } from 'date-fns'

import { isUnclosedSessionStatus } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/constants'
import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { CalendarAppointmentsView } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { useWithClient } from './useWithClient'
import { appointmentStatuses } from '../../../common/constants/appConstants'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import { useGetNotesQuery } from '../../../lyraTherapy/clientNotes/data/notesApi'
import { useGetAppointmentsQuery } from '../../../lyraTherapy/clients/clientDetails/data/appointmentsApi'
import { toTslTaskRow } from '../../../lyraTherapy/treatmentSummaryLetter/utils/tslTaskUtils'
import {
  useGetProviderManualTasksQuery,
  useGetProviderTslTasksQuery,
} from '../../../providers/dashboard/data/providerTasksApi'
import { OPEN_ITEMS_LOOKBACK_DAYS, OPEN_ITEMS_LOOKBACK_MONTHS } from '../constants'
import { ManualTaskItem, TaskItem, TslTaskItem } from '../types'

const EXCLUDED_STATUSES = [appointmentStatuses.canceled, appointmentStatuses.missed]

export const useProviderTasks: () => {
  tasks: TaskItem[]
  isLoading: boolean
} = () => {
  const intl = useIntl()
  const userId = useSelector(getAuthUserId)
  const { isTSLEnabled } = useFlags()

  const { data: appointments, isLoading: isLoadingAppointments } = useGetAppointmentsQuery({
    providerId: userId,
    durationRange: { before: { days: OPEN_ITEMS_LOOKBACK_DAYS } },
    view: CalendarAppointmentsView.CALENDAR,
  })

  const { data: notes, isLoading: isLoadingNotes } = useGetNotesQuery({
    providerId: userId,
    months: OPEN_ITEMS_LOOKBACK_MONTHS,
    note_types: [NoteTypes.INITIAL, NoteTypes.SESSION],
    excludedFields: ['body', 'content_meta_data'],
  })

  const pastAppointments = useMemo(() => {
    const cutoff = subDays(new Date(), OPEN_ITEMS_LOOKBACK_DAYS)
    return appointments?.filter(
      (appt) =>
        isPast(getDateFromAppointment(appt)) &&
        isAfter(getDateFromAppointment(appt), cutoff) &&
        !EXCLUDED_STATUSES.includes(appt.appointmentStatus),
    )
  }, [appointments])

  const { items: appointmentsWithClients, isLoading: isLoadingClients } = useWithClient(
    pastAppointments,
    (appt) => appt.userId,
  )

  const { data: manualTasksRaw, isLoading: isLoadingManualTasks } = useGetProviderManualTasksQuery({
    providerId: userId,
  })

  const { data: tslTasksRaw, isLoading: isLoadingTslTasks } = useGetProviderTslTasksQuery(
    { providerId: userId },
    { skip: !isTSLEnabled },
  )

  const tasks = useMemo<TaskItem[]>(() => {
    const apptTasks: TaskItem[] = appointmentsWithClients
      .map<TaskItem | null>((appt) => {
        const note = notes?.find((n) => n.appointment_id === appt.appointmentId)
        const noteComplete = note?.status === NoteStatusStates.COMPLETED
        if (noteComplete) {
          return null
        }
        const needsInvoice = isUnclosedSessionStatus(appt.appointmentStatus) || !!appt.isChargeReversed
        return {
          kind: 'appointment',
          client: appt.client,
          date: appt.startDate,
          needsInvoice,
          sessionNumber: appt.sessionNumber,
          appointment: appt,
        }
      })
      .filter((task): task is TaskItem => task !== null)

    const manualTasks: ManualTaskItem[] = (manualTasksRaw ?? []).map((task) => ({
      kind: 'manual',
      id: task.id,
      description: task.arguments?.description ?? '',
      client: null,
      date: task.create_datetime ?? null,
    }))

    const tslTasks: TslTaskItem[] = (tslTasksRaw ?? []).map((task) => ({
      kind: 'tsl',
      client: null,
      ...toTslTaskRow(task, intl),
    }))

    return [...apptTasks, ...manualTasks, ...tslTasks]
  }, [appointmentsWithClients, intl, notes, manualTasksRaw, tslTasksRaw])

  return {
    tasks,
    isLoading: isLoadingAppointments || isLoadingNotes || isLoadingClients || isLoadingManualTasks || isLoadingTslTasks,
  }
}
